import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, ChefHat, Loader2, Camera, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { PasswordDialog } from "@/components/PasswordDialog";
import { compressImageFile, isHeicImage, HEIC_HELP } from "@/lib/imageCompress";
import { TagInput } from "@/components/TagInput";
import { ThemeToggle } from "@/components/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ["Desserts", "Breads", "Soups", "Main Dishes", "Sides", "Preserves"];

const emptyIngredient = { name: "", amount_imperial: "", amount_metric: "", notes: "" };

export default function EditRecipePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, getAuthHeaders } = useAuth();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const photoInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [recipePhoto, setRecipePhoto] = useState(null);

  // Check auth on mount
  useEffect(() => {
    if (!isAuthenticated) {
      setPasswordDialogOpen(true);
    }
  }, [isAuthenticated]);
  
  const [formData, setFormData] = useState({
    title: "",
    source: "",
    category: "",
    servings: "",
    prep_time: "",
    cook_time: "",
    ingredients: [{ ...emptyIngredient }],
    instructions: [""],
    notes: "",
    image_url: "",
    image_data: "",
    tags: []
  });

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  const fetchRecipe = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/recipes/${id}`);
      const recipe = response.data;
      setFormData({
        title: recipe.title || "",
        source: recipe.source || "",
        category: recipe.category || "",
        servings: recipe.servings?.toString() || "",
        prep_time: recipe.prep_time || "",
        cook_time: recipe.cook_time || "",
        ingredients: recipe.ingredients?.length > 0 
          ? recipe.ingredients 
          : [{ ...emptyIngredient }],
        instructions: recipe.instructions?.length > 0 
          ? recipe.instructions 
          : [""],
        notes: recipe.notes || "",
        image_url: recipe.image_url || "",
        image_data: recipe.image_data || "",
        tags: recipe.tags || []
      });
      setRecipePhoto(recipe.image_data || null);
    } catch (error) {
      console.error("Error fetching recipe:", error);
      toast.error("Recipe not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (isHeicImage(file)) {
      toast.error("HEIC images aren't supported", { description: HEIC_HELP });
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    try {
      const compressed = await compressImageFile(file);
      setRecipePhoto(compressed);
      setFormData(prev => ({ ...prev, image_data: compressed }));
    } catch (err) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setRecipePhoto(ev.target.result);
        setFormData(prev => ({ ...prev, image_data: ev.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = () => {
    setRecipePhoto(null);
    setFormData(prev => ({ ...prev, image_data: "" }));
    if (photoInputRef.current) {
      photoInputRef.current.value = "";
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleIngredientChange = (index, field, value) => {
    const newIngredients = [...formData.ingredients];
    newIngredients[index] = { ...newIngredients[index], [field]: value };
    setFormData((prev) => ({ ...prev, ingredients: newIngredients }));
  };

  const addIngredient = () => {
    setFormData((prev) => ({
      ...prev,
      ingredients: [...prev.ingredients, { ...emptyIngredient }]
    }));
  };

  const removeIngredient = (index) => {
    if (formData.ingredients.length > 1) {
      setFormData((prev) => ({
        ...prev,
        ingredients: prev.ingredients.filter((_, i) => i !== index)
      }));
    }
  };

  const handleInstructionChange = (index, value) => {
    const newInstructions = [...formData.instructions];
    newInstructions[index] = value;
    setFormData((prev) => ({ ...prev, instructions: newInstructions }));
  };

  const addInstruction = () => {
    setFormData((prev) => ({
      ...prev,
      instructions: [...prev.instructions, ""]
    }));
  };

  const removeInstruction = (index) => {
    if (formData.instructions.length > 1) {
      setFormData((prev) => ({
        ...prev,
        instructions: prev.instructions.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error("Please enter a recipe title");
      return;
    }
    if (!formData.category) {
      toast.error("Please select a category");
      return;
    }

    // Filter out empty ingredients and instructions
    const cleanedData = {
      ...formData,
      servings: formData.servings ? parseInt(formData.servings) : null,
      ingredients: formData.ingredients.filter((ing) => ing.name.trim()),
      instructions: formData.instructions.filter((inst) => inst.trim()),
      image_data: recipePhoto || formData.image_data || null
    };

    if (cleanedData.ingredients.length === 0) {
      toast.error("Please add at least one ingredient");
      return;
    }
    if (cleanedData.instructions.length === 0) {
      toast.error("Please add at least one instruction");
      return;
    }

    setIsSaving(true);
    try {
      await axios.put(`${API}/recipes/${id}`, cleanedData, {
        headers: getAuthHeaders()
      });
      toast.success("Recipe updated successfully!");
      navigate(`/recipe/${id}`);
    } catch (error) {
      console.error("Update error:", error);
      if (error.response?.status === 401) {
        toast.error("Authentication required");
        setPasswordDialogOpen(true);
      } else {
        toast.error("Failed to update recipe", {
          description: error.response?.data?.detail || "Please try again."
        });
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
            <ChefHat className="w-6 h-6 text-primary animate-pulse" strokeWidth={1.5} />
          </div>
          <p className="text-muted-foreground">Loading recipe...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Password Dialog */}
      <PasswordDialog 
        open={passwordDialogOpen} 
        onOpenChange={(open) => {
          setPasswordDialogOpen(open);
          if (!open && !isAuthenticated) {
            navigate(`/recipe/${id}`);
          }
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/">
            <Button variant="ghost" size="icon" className="rounded-full" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </Button>
          </Link>
          <Link to="/" className="flex items-center">
            <img
              src="/logo.png"
              alt="Burnsy's Bistro"
              className="h-12 w-auto object-contain"
            />
          </Link>
          <div className="ml-auto">
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Recipe Photo */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Recipe Photo</CardTitle>
            </CardHeader>
            <CardContent>
              {recipePhoto ? (
                <div className="relative inline-block">
                  <img
                    src={recipePhoto}
                    alt="Recipe"
                    className="max-h-48 rounded-xl shadow-md"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 w-8 h-8 rounded-full"
                    onClick={removePhoto}
                    data-testid="remove-photo-btn"
                  >
                    <X className="w-4 h-4" strokeWidth={2} />
                  </Button>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:border-primary transition-colors"
                  onClick={() => photoInputRef.current?.click()}
                  data-testid="photo-upload-zone"
                >
                  <input
                    ref={photoInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoSelect}
                    className="hidden"
                    data-testid="photo-input"
                  />
                  <Camera className="w-10 h-10 mx-auto text-muted-foreground mb-3" strokeWidth={1.5} />
                  <p className="font-medium">Add a photo of your dish</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Take a photo or choose from library
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Basic Info */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="title">Recipe Title *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    placeholder="e.g., Grandma's Apple Pie"
                    className="h-12"
                    data-testid="input-title"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="category">Category *</Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleInputChange("category", value)}
                  >
                    <SelectTrigger className="h-12" data-testid="select-category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="source">Source (optional)</Label>
                  <Input
                    id="source"
                    value={formData.source}
                    onChange={(e) => handleInputChange("source", e.target.value)}
                    placeholder="e.g., Mom's cookbook"
                    className="h-12"
                    data-testid="input-source"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="servings">Servings</Label>
                  <Input
                    id="servings"
                    type="number"
                    value={formData.servings}
                    onChange={(e) => handleInputChange("servings", e.target.value)}
                    placeholder="e.g., 8"
                    className="h-12"
                    data-testid="input-servings"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="prep_time">Prep Time</Label>
                  <Input
                    id="prep_time"
                    value={formData.prep_time}
                    onChange={(e) => handleInputChange("prep_time", e.target.value)}
                    placeholder="e.g., 20 minutes"
                    className="h-12"
                    data-testid="input-prep-time"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="cook_time">Cook Time</Label>
                  <Input
                    id="cook_time"
                    value={formData.cook_time}
                    onChange={(e) => handleInputChange("cook_time", e.target.value)}
                    placeholder="e.g., 45 minutes"
                    className="h-12"
                    data-testid="input-cook-time"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Ingredients */}
          <Card className="border-border/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-serif text-xl">Ingredients</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addIngredient}
                className="rounded-full gap-1"
                data-testid="add-ingredient-btn"
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                Add
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.ingredients.map((ingredient, index) => (
                <div key={index} className="grid gap-3 md:grid-cols-12 items-start p-4 bg-secondary/30 rounded-xl">
                  <div className="md:col-span-4 space-y-1">
                    <Label className="text-xs text-muted-foreground">Ingredient</Label>
                    <Input
                      value={ingredient.name}
                      onChange={(e) => handleIngredientChange(index, "name", e.target.value)}
                      placeholder="e.g., All-purpose flour"
                      data-testid={`ingredient-name-${index}`}
                    />
                  </div>
                  <div className="md:col-span-3 space-y-1">
                    <Label className="text-xs text-muted-foreground">Imperial</Label>
                    <Input
                      value={ingredient.amount_imperial}
                      onChange={(e) => handleIngredientChange(index, "amount_imperial", e.target.value)}
                      placeholder="e.g., 2 cups"
                      data-testid={`ingredient-imperial-${index}`}
                    />
                  </div>
                  <div className="md:col-span-3 space-y-1">
                    <Label className="text-xs text-muted-foreground">Metric</Label>
                    <Input
                      value={ingredient.amount_metric}
                      onChange={(e) => handleIngredientChange(index, "amount_metric", e.target.value)}
                      placeholder="e.g., 240g"
                      data-testid={`ingredient-metric-${index}`}
                    />
                  </div>
                  <div className="md:col-span-2 flex items-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeIngredient(index)}
                      className="rounded-full text-muted-foreground hover:text-destructive mt-5"
                      disabled={formData.ingredients.length === 1}
                      data-testid={`remove-ingredient-${index}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Instructions */}
          <Card className="border-border/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-serif text-xl">Instructions</CardTitle>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addInstruction}
                className="rounded-full gap-1"
                data-testid="add-instruction-btn"
              >
                <Plus className="w-4 h-4" strokeWidth={2} />
                Add Step
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.instructions.map((instruction, index) => (
                <div key={index} className="flex gap-3 items-start">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-sm font-medium text-primary">{index + 1}</span>
                  </div>
                  <Textarea
                    value={instruction}
                    onChange={(e) => handleInstructionChange(index, e.target.value)}
                    placeholder={`Step ${index + 1}...`}
                    className="flex-1 min-h-[80px]"
                    data-testid={`instruction-${index}`}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeInstruction(index)}
                    className="rounded-full text-muted-foreground hover:text-destructive flex-shrink-0"
                    disabled={formData.instructions.length === 1}
                    data-testid={`remove-instruction-${index}`}
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Notes (optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Any tips, variations, or additional notes..."
                className="min-h-[120px]"
                data-testid="input-notes"
              />
            </CardContent>
          </Card>

          {/* Tags */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle className="font-serif text-xl">Tags (optional)</CardTitle>
            </CardHeader>
            <CardContent>
              <TagInput
                value={formData.tags}
                onChange={(tags) => handleInputChange("tags", tags)}
                placeholder="e.g. vegetarian, quick, kid-friendly — Enter to add"
              />
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex gap-4 justify-end">
            <Link to={`/recipe/${id}`}>
              <Button type="button" variant="outline" className="rounded-full px-8" data-testid="cancel-btn">
                Cancel
              </Button>
            </Link>
            <Button 
              type="submit" 
              className="rounded-full px-8 gap-2"
              disabled={isSaving}
              data-testid="save-recipe-btn"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
